import { DeleteOutlined, EditOutlined, ExclamationCircleOutlined, PlusOutlined, SaveOutlined } from "@ant-design/icons";
import { Col, Input, List, Modal, Row, Spin } from "antd";
import { useFormik } from "formik";
import { get } from "lodash";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import Button from "../../../components/functional_components/Button/PFButton";
import { setLoading } from "../../../redux/ducks/Loading";
import WbsCategory from "../../../services/api/WbsCategory";
import { paginationDefaults } from "../../../utils/properties";
import { showError, showSuccess, trimData } from "../../../utils/utilities";

const WbsCategories = () => {
  const { confirm } = Modal;
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [state, setState] = useState({
    tableContent: [],
    pagination: {
      currentPage: paginationDefaults.page,
      size: paginationDefaults.size,
      totalItems: paginationDefaults.totalItems,
    },
    loading: false,
    modal: {
      visible: false,
      type: "",
      data: {},
    },
  });

  useEffect(() => {
    dataList();
  }, []);

  const formikWbsCategory = useFormik({
    initialValues: {
      id: get(state.modal.data, "id", ""),
      name: get(state.modal.data, "name", ""),
    },
    validate: (values) => {
      values = trimData(values);
      let errors = {};
      if (!values.id) {
        errors.id = "Required!";
      }
      if (!values.name) {
        errors.name = "Required!";
      }
      return errors;
    },
    enableReinitialize: true,
    onSubmit: async (values) => {
      setState((prevState) => ({ ...prevState, loading: true }));
      const response = await WbsCategory.saveWbsCategory(values);
      if (!response.error) {
        showSuccess();
        dataList();
        closeModal();
      } else {
        showError(get(response, "error.messageCode", "ER000"));
      }
      setState((prevState) => ({ ...prevState, loading: false }));
    },
  });

  const dataList = async (page = state.pagination.currentPage) => {
    dispatch(setLoading(true));
    const wbsCategories = await WbsCategory.getWbsCategoriesList(`page=${page - 1}&size=${state.pagination.size}`);
    if (!wbsCategories.error && wbsCategories.data) {
      wbsCategories.data.forEach((wbsCategory, i) => {
        wbsCategory.key = i; // This is necessary to remove warning in console for unique key row
      });
      setState((prevState) => ({
        ...prevState,
        tableContent: get(wbsCategories, "data", []),
        pagination: {
          currentPage: wbsCategories.currentPage ? wbsCategories.currentPage + 1 : paginationDefaults.page,
          size: get(wbsCategories, "pageSize", paginationDefaults.size),
          totalItems: get(wbsCategories, "totalItems", paginationDefaults.totalItems),
        },
      }));
    } else {
      showError(get(wbsCategories, "error.messageCode", "ER000"));
    }
    dispatch(setLoading(false));
  };

  const editCallback = async (id) => {
    setState((prevState) => ({ ...prevState, loading: true, modal: { visible: true, type: "edit" } }));
    const currentWbsCategory = await WbsCategory.getWbsCategoriesList(`id=${id}`);
    if (!currentWbsCategory.error && currentWbsCategory.data) {
      setState((prevState) => ({
        ...prevState,
        modal: { visible: true, type: "edit", data: currentWbsCategory.data[0] },
      }));
    } else {
      showError(get(currentWbsCategory, "error.messageCode", "ER000"));
    }
    setState((prevState) => ({ ...prevState, loading: false }));
  };

  const deleteCallback = async (wbsCategoryId) => {
    confirm({
      title: t("general.ensureDelete"),
      icon: <ExclamationCircleOutlined />,
      onOk: async () => {
        dispatch(setLoading(true));
        const deleteResponse = await WbsCategory.deleteWbsCategory(wbsCategoryId);
        if (!deleteResponse.error) {
          showSuccess();
          dataList();
        } else {
          showError(get(deleteResponse, "error.messageCode", "ER000"));
        }
        dispatch(setLoading(false));
      },
      onCancel() {},
    });
  };

  const closeModal = () => {
    formikWbsCategory.resetForm();
    setState((prevState) => ({ ...prevState, modal: { visible: false, type: "", data: {} } }));
  };

  return (
    <>
      <Row type="flex" align="middle" justify="end" className="mb-10">
        <Button
          currentIcon={<PlusOutlined className="icon-medium" />}
          clickCallback={() => setState((prevState) => ({ ...prevState, modal: { visible: true, type: "new" } }))}
        />
      </Row>
      <List
        header={<strong>{t("menuLeft.wbsCategories")}</strong>}
        size="small"
        bordered
        pagination={{
          size: "small",
          current: state.pagination.currentPage,
          pageSize: state.pagination.size,
          total: state.pagination.totalItems,
          onChange: (page, pageSize) => {
            setState((prevState) => ({
              ...prevState,
              pagination: {
                currentPage: page,
                size: pageSize,
                totalItems: state.pagination.totalItems,
              },
            }));
            dataList(page);
          },
        }}
        dataSource={state.tableContent}
        renderItem={(item) => (
          <List.Item
            actions={[
              <Row className="list-actions" key={item.id}>
                <Col key={item.id} onClick={() => editCallback(item.id)}>
                  <EditOutlined className="icon-medium mr-10" />
                </Col>
                {!item.deletable ? (
                  <Col>
                    {" "}
                    <DeleteOutlined className="icon-medium disabled no-pointer" />{" "}
                  </Col>
                ) : (
                  <Col onClick={() => deleteCallback(item.id)}>
                    {" "}
                    <DeleteOutlined className="icon-medium" />
                  </Col>
                )}
              </Row>,
            ]}
          >
            <List.Item.Meta title={<div>{`${item.id} | ${item.name}`}</div>} />
          </List.Item>
        )}
      />

      <Modal
        open={state.modal.visible}
        width={700}
        title={
          state.modal.type === "edit" && state.modal.data && state.modal.data.id
            ? state.modal.data.id
            : t("wbs.newWbsCategory")
        }
        footer={[
          <Button
            key="okButton"
            currentIcon={<SaveOutlined className="icon-medium" />}
            clickCallback={formikWbsCategory.submitForm}
            isDisabled={!(formikWbsCategory.dirty && formikWbsCategory.isValid)}
          />,
        ]}
        onCancel={closeModal}
      >
        {state.loading ? (
          <div className="container-spinner-positioning">
            <Spin spinning={state.loading} className="spinner-positioning" />
          </div>
        ) : (
          ""
        )}
        <Row gutter={[40, 24]}>
          <Col lg={10} md={10} xs={24} sm={10}>
            <Row>
              <label className="fw-bold mt-10" htmlFor="id">
                {t("costCenters.table.id")}*
              </label>
            </Row>
            <Row>
              <Input
                required
                id="id"
                name="id"
                onChange={formikWbsCategory.handleChange}
                onBlur={formikWbsCategory.handleBlur}
                value={formikWbsCategory.values.id}
                disabled={state.modal.type === "edit" ? true : false}
              />
            </Row>
          </Col>
          <Col lg={10} md={10} xs={24} sm={10}>
            <Row>
              <label className="fw-bold mt-10" htmlFor="name">
                {t("costCenters.table.name")}*
              </label>
            </Row>
            <Row>
              <Input
                required
                id="name"
                name="name"
                onChange={formikWbsCategory.handleChange}
                onBlur={formikWbsCategory.handleBlur}
                value={formikWbsCategory.values.name}
              />
            </Row>
          </Col>
        </Row>
      </Modal>
    </>
  );
};

export default WbsCategories;
